// b548 — UAS / DRONE layer on the Cesium 3D globe. Plots FAA Remote ID detections from
// /drones (the app's AirWardenManager pushes them; demo fallback near view center).
// Each drone: marker at its altitude + vertical line to ground (height) + label
// (serial + alt) + red threat ring on the ground if it's an unregistered/threat UAS.
import { createEffect, onCleanup } from "solid-js";
import { useCesium } from "../../cesium/useCesium.ts";
import { groundState } from "./store.ts";
import { PROXY_BASE_URL } from "../../config.ts";
const NODEPTH = Number.POSITIVE_INFINITY;
export function DroneLayer() {
  const {
    viewer,
    ready
  } = useCesium();
  let ds = null;
  let timer = null;
  const cart = (lon, lat, alt = 0) => Cesium.Cartesian3.fromDegrees(lon, lat, alt);
  const clear = () => {
    const v = viewer();
    if (v && ds) {
      try {
        v.dataSources.remove(ds, true);
      } catch (_) {}
    }
    ds = null;
  };
  const centerQuery = () => {
    const v = viewer();
    if (!v) return "";
    try {
      const ell = v.scene.globe.ellipsoid;
      const c = v.camera.pickEllipsoid(new Cesium.Cartesian2(v.canvas.clientWidth / 2, v.canvas.clientHeight / 2), ell);
      const cg = c ? ell.cartesianToCartographic(c) : v.camera.positionCartographic;
      if (!cg) return "";
      return `?lat=${Cesium.Math.toDegrees(cg.latitude).toFixed(5)}&lon=${Cesium.Math.toDegrees(cg.longitude).toFixed(5)}`;
    } catch (_) {
      return "";
    }
  };
  const poll = async () => {
    const v = viewer();
    if (!v || !groundState.dronesEnabled) return;
    let r;
    try {
      r = await (await fetch(`${PROXY_BASE_URL}/drones${centerQuery()}`)).json();
    } catch (e) {
      return;
    }
    const drones = r && r.drones || [];
    clear();
    ds = new Cesium.CustomDataSource("drones");
    v.dataSources.add(ds);
    drones.forEach(d => {
      const col = Cesium.Color.fromCssColorString(d.isThreat ? "#ff2db5" : "#ff8ad6");
      const alt = d.alt || 100;
      ds.entities.add({
        position: cart(d.lon, d.lat, alt),
        point: {
          pixelSize: 11,
          color: col,
          outlineColor: Cesium.Color.WHITE,
          outlineWidth: 1.5,
          disableDepthTestDistance: NODEPTH
        },
        label: {
          text: `⊗ UAS ${d.serial || d.id} ${Math.round(alt)}m`,
          font: "bold 11px monospace",
          fillColor: col,
          style: Cesium.LabelStyle.FILL_AND_OUTLINE,
          outlineColor: Cesium.Color.BLACK,
          outlineWidth: 3,
          pixelOffset: new Cesium.Cartesian2(0, -16),
          disableDepthTestDistance: NODEPTH
        }
      });
      ds.entities.add({
        polyline: {
          positions: [cart(d.lon, d.lat, alt), cart(d.lon, d.lat, 0)],
          width: 1.5,
          material: col.withAlpha(0.5)
        }
      });
      if (d.isThreat) {
        ds.entities.add({
          position: cart(d.lon, d.lat, 0),
          ellipse: {
            semiMajorAxis: 120,
            semiMinorAxis: 120,
            material: Cesium.Color.RED.withAlpha(0.16),
            outline: true,
            outlineColor: Cesium.Color.RED.withAlpha(0.7)
          }
        });
      }
    });
    v.scene.requestRender();
  };
  createEffect(() => {
    if (!ready()) return;
    if (groundState.dronesEnabled) {
      poll();
      if (timer) clearInterval(timer);
      timer = setInterval(poll, 5000); // Remote ID refresh
    } else {
      clear();
      if (timer) {
        clearInterval(timer);
        timer = null;
      }
    }
  });
  onCleanup(() => {
    clear();
    if (timer) clearInterval(timer);
  });
  return null;
}
export default DroneLayer;